<script lang="ts">
  import { untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import type {
    EmployeeOption,
    WorkingHoursByUser,
  } from "../../../../../../../_shared/_logic/form-state"
  import {
    ceilPositiveMinutesToStep,
    SCHEDULING_MINUTE_STEP,
  } from "../../../../../../../_shared/_logic/time-granularity"
  import {
    buildRescheduleEmployeeOptions,
    createInitialRescheduleDraft,
    evaluateRescheduleAvailabilityHint,
    validateRescheduleDraft,
  } from "./_logic/rescheduleDraft"
  import { CONFIRMATION_EMAIL_OPTIONS } from "../../_logic/confirmation-email-choice"

  type Props = {
    event: ScheduleEvent
    workOrder?: WorkOrder | null
    employees: EmployeeOption[]
    scheduleEvents?: ScheduleEvent[]
    workingHoursByUser?: WorkingHoursByUser
    pending?: boolean
    error?: string | null
    onReschedule: (options: {
      updatePayload: ScheduleEventUpdatePayload
      sendConfirmationEmail: boolean
    }) => void | Promise<void>
    onClose: () => void
  }

  const {
    event,
    workOrder = null,
    employees = [],
    scheduleEvents = [],
    workingHoursByUser = {},
    pending = false,
    error = null,
    onReschedule,
    onClose,
  }: Props = $props()

  let draft = $state(
    untrack(() =>
      createInitialRescheduleDraft({
        event,
        workOrder,
        employees,
      }),
    ),
  )

  const employeeOptions = $derived(
    buildRescheduleEmployeeOptions({
      employees,
      workOrder,
      currentEmployeeId: event.employee_id,
    }),
  )
  const validation = $derived(
    validateRescheduleDraft({
      draft,
      event,
      employeeOptions,
    }),
  )
  const availabilityHint = $derived(
    evaluateRescheduleAvailabilityHint({
      draft,
      event,
      scheduleEvents,
      workingHoursByUser,
    }),
  )
  const validationError = $derived(validation.ok === false ? validation.error : null)

  const handleDurationChange = (changeEvent: Event) => {
    const target = changeEvent.target as HTMLInputElement | null
    const next = Number(target?.value ?? NaN)

    if (!Number.isFinite(next) || next <= 0) return

    const normalized =
      ceilPositiveMinutesToStep(next, SCHEDULING_MINUTE_STEP) ?? SCHEDULING_MINUTE_STEP

    if (target) target.value = String(normalized)

    draft = { ...draft, durationMinutes: normalized }
  }

  const submit = () => {
    if (pending || !validation.ok) return

    void onReschedule({
      updatePayload: validation.updatePayload,
      sendConfirmationEmail: validation.sendConfirmationEmail,
    })
  }
</script>

<ModalDialog appearance="plain" title="Termin verschieben" size="large" {onClose}>
  <div class="reschedule-dialog-form form">
    <div class="form-row">
      <Input
        surfaceTone="light"
        brightBackground={true}
        type="datetime-local"
        label="Neuer Terminzeitpunkt"
        placeholder="Start"
        minuteIncrement={SCHEDULING_MINUTE_STEP}
        bind:value={draft.startAt}
        disabled={pending}
      />
      <Input
        surfaceTone="light"
        brightBackground={true}
        type="number"
        label="Dauer (Minuten)"
        placeholder="Dauer (Minuten)"
        value={draft.durationMinutes}
        min={SCHEDULING_MINUTE_STEP}
        step={SCHEDULING_MINUTE_STEP}
        onChange={handleDurationChange}
        disabled={pending}
      />
    </div>
    <div class="form-row">
      <Input
        surfaceTone="light"
        brightBackground={true}
        type="select"
        label="Mitarbeiter"
        placeholder="Mitarbeiter"
        options={employeeOptions}
        bind:value={draft.employeeId}
        disabled={pending}
      />
      <Input
        surfaceTone="light"
        brightBackground={true}
        type="select"
        label="Terminbestätigung"
        options={CONFIRMATION_EMAIL_OPTIONS}
        bind:value={draft.emailChoice}
        disabled={pending}
      />
    </div>
    {#if availabilityHint}
      <p class="dialog-hint" role="status">{availabilityHint}</p>
    {/if}
    {#if error || validationError}
      <p class="dialog-error" role="alert">{error ?? validationError}</p>
    {/if}
  </div>
  {#snippet footerSnippet()}
    <div class="dialog-actions">
      <CTA
        darkMode={false}
        type="primaryButton"
        content={pending ? "Wird verschoben..." : "Termin verschieben"}
        disabled={pending || !validation.ok}
        onClick={submit}
      />
    </div>
  {/snippet}
</ModalDialog>

<style>
  .reschedule-dialog-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    color: var(--text-invers-100, #2f4858);
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 16rem), 1fr));
    gap: 1rem;
    align-items: start;
    width: 100%;
    min-width: 0;
  }

  .form-row > :global(*) {
    min-width: 0;
    max-width: 100%;
  }

  :global(.reschedule-dialog-form label) {
    height: auto !important;
    max-height: none !important;
    min-height: 60px;
    min-width: 0;
  }

  :global(.reschedule-dialog-form .svelte-select),
  :global(.reschedule-dialog-form .value-container),
  :global(.reschedule-dialog-form .selected-item),
  :global(.reschedule-dialog-form .select-selection),
  :global(.reschedule-dialog-form .select-selection__label) {
    white-space: normal !important;
    overflow-wrap: anywhere;
    height: auto !important;
  }

  .dialog-hint {
    margin: 0;
    color: var(--warning-100, #975a16) !important;
    font-size: 0.9rem;
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  .dialog-error {
    margin: 0;
    color: var(--error-100, #c53030) !important;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .dialog-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.6rem;
    width: 100%;
  }
</style>

